import { createContext, useContext, useState, useEffect } from 'react';

// Import translation files
import en from './locales/en.json';
import ru from './locales/ru.json';
import tk from './locales/tk.json';

const translations = { en, ru, tk };

// Get browser language
const getBrowserLanguage = () => {
  const browserLang = navigator.language.slice(0, 2);
  return ['en', 'ru', 'tk'].includes(browserLang) ? browserLang : 'en';
};

// Get initial language from localStorage or browser
const getInitialLanguage = () => {
  return localStorage.getItem('language') || getBrowserLanguage();
};

// Flatten nested translation object
const flattenTranslations = (obj, prefix = '') => {
  const flattened = {};
  
  for (const key in obj) {
    const value = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;
    
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(flattened, flattenTranslations(value, newKey));
    } else {
      flattened[newKey] = value;
    }
  }
  
  return flattened;
};

// Create context
const I18nContext = createContext();

// Provider component
export const I18nProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage());
  const [flatTranslations, setFlatTranslations] = useState({});
  const [rawTranslations, setRawTranslations] = useState({});

  useEffect(() => {
    // Flatten translations for current language
    const flattened = flattenTranslations(translations[language]);
    setFlatTranslations(flattened);
    setRawTranslations(translations[language]);
    
    // Save to localStorage
    localStorage.setItem('language', language);
    
    // Update HTML lang attribute
    document.documentElement.lang = language;
  }, [language]);

  const t = (key, options = {}) => {
    // If returnObjects is true, return the raw object/array
    if (options.returnObjects) {
      const keys = key.split('.');
      let result = rawTranslations;
      
      for (const k of keys) {
        if (result && typeof result === 'object') {
          result = result[k];
        } else {
          return options.defaultValue || [];
        }
      }
      
      return Array.isArray(result) ? result : (result || options.defaultValue || []);
    }
    
    // Default: return string
    return flatTranslations[key] || key;
  };

  const changeLanguage = (newLang) => {
    if (translations[newLang]) {
      setLanguage(newLang);
    }
  };

  const value = {
    language,
    changeLanguage,
    t,
    availableLanguages: ['en', 'ru', 'tk']
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

// Custom hook
export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};